import { RefObject } from 'react'
import * as htmlToImage from 'html-to-image'
import { PDFDocument, PDFFont, PDFImage, PDFPage, rgb } from 'pdf-lib'
import fontkit from '@pdf-lib/fontkit'
import { useNotification } from '@/modules/context/NotificationContext.tsx'
import { useTranslation } from 'react-i18next'

// QR code — affiche (1 copy)
const QR_AFFICHE_SIZE: number = 130
const QR_AFFICHE_X: number = 351
const QR_AFFICHE_Y: number = 55

// Name text — affiche (centered horizontally)
const TEXT_AFFICHE_Y: number = 975
const TEXT_AFFICHE_SIZE: number = 26

// Logo — affiche (top right)
const LOGO_AFFICHE_MAX_SIZE: number = 124
const LOGO_AFFICHE_X: number = 694
const LOGO_AFFICHE_Y: number = 1051

// QR code — flyers (4 copies, 2×2 grid)
const QR_FLYERS_SIZE: number = 65
const QR_FLYERS_POSITIONS: { x: number; y: number }[] = [
    { x: 176, y: 627 }, // top-right copy
    { x: 597, y: 627 }, // top-left copy
    { x: 176, y: 32 }, // bottom-left copy
    { x: 597, y: 32 } // bottom-right copy
]

// Name text — flyers (one per copy)
const TEXT_FLYERS_SIZE: number = 16
const TEXT_FLYERS_POSITIONS: { x: number; y: number }[] = [
    { x: 210, y: 1086 }, // top-left copy
    { x: 630, y: 1086 }, // top-right copy
    { x: 210, y: 492 }, // bottom-left copy
    { x: 630, y: 492 } // bottom-right copy
]

// Logo — flyers (top right of each copy)
const LOGO_FLYERS_MAX_SIZE: number = 62
const LOGO_FLYERS_POSITIONS: { x: number; y: number }[] = [
    { x: 348, y: 1125 }, // top-left copy
    { x: 769, y: 1125 }, // top-right copy
    { x: 348, y: 531 }, // bottom-left copy
    { x: 769, y: 531 } // bottom-right copy
]

interface UseCommunicationKitPdfOptions {
    pdfVariant: 'eaje' | 'rpe' | 'staff'
    daycareName?: string
    daycareLogoBase64Data?: string
    qrContainerRef: RefObject<HTMLDivElement | null>
    qrExactRef: RefObject<HTMLDivElement | null>
}

function base64ToBytes(base64Data: string): { bytes: Uint8Array; isJpeg: boolean } {
    const raw: string = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data
    const binary: string = atob(raw)
    const bytes: Uint8Array = new Uint8Array(binary.length)
    for (let i: number = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i)
    }
    // Detect from magic bytes: JPEG starts with FF D8 FF, PNG with 89 50 4E 47
    const isJpeg: boolean = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff
    return { bytes, isJpeg }
}

function scaleToFit(imgWidth: number, imgHeight: number, maxSize: number): { w: number; h: number } {
    const ratio: number = imgWidth / imgHeight
    return ratio > 1 ? { w: maxSize, h: maxSize / ratio } : { w: maxSize * ratio, h: maxSize }
}

// drawImage positions the image's bottom-left corner, so a scaled-down (non-square) image must be
// re-centered in the boxX/boxY, boxSize×boxSize slot it was fitted into, or it hugs that corner.
function centerInBox(boxX: number, boxY: number, boxSize: number, w: number, h: number): { x: number; y: number } {
    return { x: boxX + (boxSize - w) / 2, y: boxY + (boxSize - h) / 2 }
}

function triggerDownload(url: string, filename: string): void {
    const link: HTMLAnchorElement = document.createElement('a')
    link.href = url
    link.download = filename
    link.click()
}

export function useCommunicationKitPdf({
    pdfVariant,
    daycareName,
    daycareLogoBase64Data,
    qrContainerRef,
    qrExactRef
}: UseCommunicationKitPdfOptions): {
    handleSaveToPng: () => Promise<void>
    handleDownloadPdf: (template: 'flyers' | 'affiche') => Promise<void>
} {
    const { t } = useTranslation()
    const { showNotificationSuccess, showNotificationError } = useNotification()

    async function handleSaveToPng(): Promise<void> {
        const contentToSave: HTMLDivElement | null = qrContainerRef.current
        if (!contentToSave) return

        try {
            const dataUrl: string = await htmlToImage.toPng(contentToSave)
            triggerDownload(dataUrl, 'qrcode.png')
            showNotificationSuccess(t('qrCodeSaved'))
        } catch {
            showNotificationError(t('errorSavingQRCode'))
        }
    }

    async function handleDownloadPdf(template: 'flyers' | 'affiche'): Promise<void> {
        const contentToSave: HTMLDivElement | null = qrExactRef.current
        if (!contentToSave) return

        try {
            const templatePath: string = `/templates/${template}_${pdfVariant}.pdf`

            const [qrDataUrl, templateBytes, fontBytes] = await Promise.all([
                htmlToImage.toPng(contentToSave, { skipFonts: true }),
                fetch(templatePath).then((r) => r.arrayBuffer()),
                fetch('/fonts/BobbyJones.otf').then((r) => r.arrayBuffer())
            ])

            const pdfDoc: PDFDocument = await PDFDocument.load(templateBytes)
            pdfDoc.registerFontkit(fontkit)

            const { bytes: qrBytes } = base64ToBytes(qrDataUrl)
            const [qrImage, font]: [PDFImage, PDFFont] = await Promise.all([
                pdfDoc.embedPng(qrBytes),
                pdfDoc.embedFont(fontBytes)
            ])

            const logoImage: PDFImage | null = daycareLogoBase64Data
                ? await (async () => {
                      const { bytes, isJpeg } = base64ToBytes(daycareLogoBase64Data)
                      return isJpeg ? pdfDoc.embedJpg(bytes) : pdfDoc.embedPng(bytes)
                  })()
                : null

            const page: PDFPage = pdfDoc.getPages()[0]

            if (template === 'affiche') {
                page.drawImage(qrImage, {
                    x: QR_AFFICHE_X,
                    y: QR_AFFICHE_Y,
                    width: QR_AFFICHE_SIZE,
                    height: QR_AFFICHE_SIZE
                })
                if (daycareName) {
                    const textWidth: number = font.widthOfTextAtSize(daycareName, TEXT_AFFICHE_SIZE)
                    page.drawText(daycareName, {
                        x: (page.getWidth() - textWidth) / 2,
                        y: TEXT_AFFICHE_Y,
                        size: TEXT_AFFICHE_SIZE,
                        color: rgb(0.98046875, 0.64453125, 0.1875), // RGB(251, 165, 48)
                        font
                    })
                }
                if (logoImage) {
                    const { w, h } = scaleToFit(logoImage.width, logoImage.height, LOGO_AFFICHE_MAX_SIZE)
                    const { x, y } = centerInBox(LOGO_AFFICHE_X, LOGO_AFFICHE_Y, LOGO_AFFICHE_MAX_SIZE, w, h)
                    page.drawImage(logoImage, { x, y, width: w, height: h })
                }
            } else {
                for (let i: number = 0; i < QR_FLYERS_POSITIONS.length; i++) {
                    page.drawImage(qrImage, {
                        x: QR_FLYERS_POSITIONS[i].x,
                        y: QR_FLYERS_POSITIONS[i].y,
                        width: QR_FLYERS_SIZE,
                        height: QR_FLYERS_SIZE
                    })
                    if (daycareName) {
                        const textWidth: number = font.widthOfTextAtSize(daycareName, TEXT_FLYERS_SIZE)
                        page.drawText(daycareName, {
                            x: TEXT_FLYERS_POSITIONS[i].x - textWidth / 2,
                            y: TEXT_FLYERS_POSITIONS[i].y,
                            size: TEXT_FLYERS_SIZE,
                            color: rgb(0.98046875, 0.64453125, 0.1875), // RGB(251, 165, 48)
                            font
                        })
                    }
                    if (logoImage) {
                        const { w, h } = scaleToFit(logoImage.width, logoImage.height, LOGO_FLYERS_MAX_SIZE)
                        const { x, y } = centerInBox(
                            LOGO_FLYERS_POSITIONS[i].x,
                            LOGO_FLYERS_POSITIONS[i].y,
                            LOGO_FLYERS_MAX_SIZE,
                            w,
                            h
                        )
                        page.drawImage(logoImage, { x, y, width: w, height: h })
                    }
                }
            }

            const pdfBytes: Uint8Array<ArrayBufferLike> = await pdfDoc.save()
            const blob: Blob = new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' })
            const objectUrl: string = URL.createObjectURL(blob)

            triggerDownload(objectUrl, `${template}_${pdfVariant}.pdf`)
            URL.revokeObjectURL(objectUrl)

            showNotificationSuccess(t('pdfDownloaded'))
        } catch (e) {
            showNotificationError(t('errorGeneratingPdf'))
            console.log(e)
        }
    }

    return { handleSaveToPng, handleDownloadPdf }
}
